import React, { useEffect, useRef, useState } from "react";
import { Download, FileText, Loader2, Pencil, Plus, Upload } from "lucide-react";
import Eye from "lucide-react/dist/esm/icons/eye";
import Archive from "lucide-react/dist/esm/icons/archive";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { ukDate } from "../../lib/format";
import { formatApiError } from "../../lib/errors";
import { fmtBytes, isPdfFile, MAX_PDF_BYTES } from "../../lib/subby";
import { Card, Input, Modal, PrimaryButton, QuietButton } from "../../components/UI";
import { PdfViewerModal } from "../../components/PdfViewerModal";
import { BluebeamButton, useBluebeamLauncher } from "../../components/BluebeamOpen";

const DND_KEY = "application/x-lux-drawing";
const CATS = [
  { key: "drawings",    label: "Drawings",    hint: "Architectural drawings" },
  { key: "structural",  label: "Structural",  hint: "Structural engineer's drawings" },
  { key: "lux_markups", label: "LUX Markups", hint: "Your marked-up copies — kept apart from the originals" },
];

async function downloadDrawing(tenderId, d) {
  const r = await api.get(`/tenders/${tenderId}/drawings/${d.id}/file`, { params: { download: 1 }, responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url; a.download = d.filename || `${d.title}.pdf`;
  document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(url);
}

/** Authenticated thumbnail — fetched as a blob because <img src> cannot carry the bearer token. */
function Thumb({ tenderId, drawing }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(!drawing.thumb_gridfs_id);
  useEffect(() => {
    if (!drawing.thumb_gridfs_id) return undefined;
    let alive = true; let obj = null;
    api.get(`/tenders/${tenderId}/drawings/${drawing.id}/thumbnail`, { responseType: "blob" })
      .then((r) => { if (!alive) return; obj = window.URL.createObjectURL(r.data); setUrl(obj); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; if (obj) window.URL.revokeObjectURL(obj); };
  }, [tenderId, drawing.id, drawing.thumb_gridfs_id]);
  if (failed || !url) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center text-ink-4 gap-1" data-testid={`drawing-${drawing.id}-thumb-placeholder`}>
        <FileText size={28} strokeWidth={1.2} />
        <span style={{ fontSize: 10 }}>{failed ? "PDF" : "…"}</span>
      </div>
    );
  }
  return <img src={url} alt="" draggable={false} className="w-full h-full object-contain pointer-events-none" data-testid={`drawing-${drawing.id}-thumb`} />;
}

function RenameDialog({ open, drawing, onClose, onSaved, tenderId }) {
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setTitle(drawing?.title || ""); setBusy(false); } }, [open, drawing]);
  if (!drawing) return null;
  const save = async () => {
    if (!title.trim()) return;
    setBusy(true);
    try {
      const r = await api.patch(`/tenders/${tenderId}/drawings/${drawing.id}`, { title: title.trim() });
      onSaved(r.data); onClose(); toast.success("Drawing renamed");
    } catch (e) { toast.error(formatApiError(e, "Rename failed")); }
    finally { setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title="Rename drawing" testid="drawing-rename"
      footer={<>
        <QuietButton onClick={onClose} data-testid="drawing-rename-cancel">Cancel</QuietButton>
        <PrimaryButton onClick={save} disabled={!title.trim() || busy} data-testid="drawing-rename-save">Save</PrimaryButton>
      </>}>
      <Input value={title} onChange={setTitle} placeholder="e.g. A-101 Ground floor plan" data-testid="drawing-rename-input" />
      <div className="text-ink-4 mt-2" style={{ fontSize: 11 }}>File: <span className="font-mono">{drawing.filename}</span> — the file itself is never changed.</div>
    </Modal>
  );
}

/** Tile menu — reduced to View · Bluebeam · Download · Rename (archived: View · Download). */
function DrawingActions({ d, tenderId, onView, onRename, bb }) {
  const b = "inline-flex items-center gap-1 h-7 px-2 rounded text-ink-2 hover:text-ink hover:bg-surface-2 border border-line";
  const dl = async () => { try { await downloadDrawing(tenderId, d); } catch (e) { toast.error(formatApiError(e, "Download failed")); } };
  if (d.deleted_at) {
    return (
      <div className="flex flex-wrap items-center gap-1" style={{ fontSize: 11 }}>
        <button type="button" className={b} onClick={() => onView(d)} data-testid={`drawing-${d.id}-view`}><Eye size={11} /> View</button>
        <button type="button" className={b} onClick={dl} data-testid={`drawing-${d.id}-download`}><Download size={11} /> Download</button>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-1" style={{ fontSize: 11 }}>
      <button type="button" className={b} onClick={() => onView(d)} data-testid={`drawing-${d.id}-view`}><Eye size={11} /> View</button>
      <BluebeamButton tenderId={tenderId} drawing={d} onDownload={dl} compact bb={bb} />
      <button type="button" className={b} onClick={dl} data-testid={`drawing-${d.id}-download`}><Download size={11} /></button>
      <button type="button" className={b} onClick={() => onRename(d)} title="Rename" data-testid={`drawing-${d.id}-rename`}><Pencil size={11} /></button>
    </div>
  );
}

function Tile({ tenderId, d, onView, onRename, bb }) {
  return (
    <div
      draggable
      onDragStart={(e) => { e.dataTransfer.setData(DND_KEY, d.id); e.dataTransfer.effectAllowed = "move"; }}
      className={["rounded-lg border bg-surface overflow-hidden flex flex-col cursor-grab active:cursor-grabbing", d.deleted_at ? "opacity-70 border-line" : "border-line hover:border-accent"].join(" ")}
      data-testid={`drawing-${d.id}`} data-category={d.category || "drawings"}>
      <button type="button" draggable={false} onClick={() => onView(d)} className="block w-full bg-surface-2/60 border-b border-line-soft" style={{ height: 170 }} title="Open" data-testid={`drawing-${d.id}-open`}>
        <Thumb tenderId={tenderId} drawing={d} />
      </button>
      <div className="p-3 flex-1 flex flex-col gap-2">
        <div className="min-w-0">
          <div className="text-ink truncate" style={{ fontSize: 13, fontWeight: 600 }} title={d.title} data-testid={`drawing-${d.id}-title`}>{d.title}</div>
          <div className="text-ink-3 truncate" style={{ fontSize: 10.5 }}>{d.page_count ? `${d.page_count} page${d.page_count === 1 ? "" : "s"} · ` : ""}{fmtBytes(d.size_bytes)} · {ukDate(d.uploaded_at)}{d.uploaded_by_name ? ` · ${d.uploaded_by_name}` : ""}</div>
          {d.deleted_at && <div style={{ fontSize: 10.5, color: "#7A1F1F" }} data-testid={`drawing-${d.id}-archived`}>Archived {ukDate(d.deleted_at)} — drag onto a section to restore</div>}
        </div>
        <DrawingActions d={d} tenderId={tenderId} onView={onView} onRename={onRename} bb={bb} />
      </div>
    </div>
  );
}

const GRID = { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12 };

export function DrawingsTab({ tender }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploads, setUploads] = useState([]);
  const [viewer, setViewer] = useState(null);
  const [rename, setRename] = useState(null);
  const [dragCat, setDragCat] = useState(null);   // section key currently hovered during a drag
  const inputRef = useRef(null);
  const uploadTarget = useRef("drawings");
  const bb = useBluebeamLauncher();

  const load = () => {
    api.get(`/tenders/${tender.id}/drawings`, { params: { include_deleted: true } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e, "Failed to load drawings")))
      .finally(() => setLoading(false));
  };
  useEffect(load, [tender.id]); // eslint-disable-line

  const replace = (d) => setData((cur) => (cur ? { ...cur, drawings: cur.drawings.map((x) => (x.id === d.id ? d : x)) } : cur));

  const uploadFiles = async (files, category = "drawings") => {
    const list = Array.from(files || []);
    if (!list.length) return;
    const label = CATS.find((c) => c.key === category)?.label || "Drawings";
    setUploads(list.map((f) => ({ name: f.name, state: "queued", error: null })));
    let ok = 0;
    for (let i = 0; i < list.length; i += 1) {
      const f = list[i];
      const mark = (state, error = null) => setUploads((u) => u.map((r, j) => (j === i ? { ...r, state, error } : r)));
      if (!isPdfFile(f)) { mark("failed", "Not a PDF"); continue; }
      if (f.size > MAX_PDF_BYTES) { mark("failed", "Over the 10 MB limit"); continue; }
      mark("uploading");
      const fd = new FormData();
      fd.append("file", f);
      fd.append("category", category);
      try {
        await api.post(`/tenders/${tender.id}/drawings`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        mark("done"); ok += 1;
      } catch (e) { mark("failed", formatApiError(e, "Upload failed")); }
    }
    if (ok) toast.success(`${ok} PDF${ok === 1 ? "" : "s"} added to ${label}`);
    load();
    setTimeout(() => setUploads((u) => (u.every((r) => r.state === "done") ? [] : u)), 2500);
  };

  const pickUpload = (category) => { uploadTarget.current = category; inputRef.current?.click(); };

  const moveTo = async (id, category) => {
    const d = (data?.drawings || []).find((x) => x.id === id);
    if (!d) return;
    const wasArchived = !!d.deleted_at;
    if (!wasArchived && (d.category || "drawings") === category) return;
    try {
      if (wasArchived) await api.post(`/tenders/${tender.id}/drawings/${id}/restore`);
      await api.patch(`/tenders/${tender.id}/drawings/${id}`, { category });
      const label = CATS.find((c) => c.key === category)?.label || "Drawings";
      toast.success(wasArchived ? `Restored to ${label}` : `Moved to ${label}`);
      load();
    } catch (e) { toast.error(formatApiError(e, "Move failed")); }
  };

  const archive = async (id) => {
    const d = (data?.drawings || []).find((x) => x.id === id);
    if (!d || d.deleted_at) return;
    try { await api.post(`/tenders/${tender.id}/drawings/${id}/delete`); toast.success(`"${d.title}" moved to Archive — the file is kept`); load(); }
    catch (e) { toast.error(formatApiError(e, "Archive failed")); }
  };

  const sectionDrop = (e, category) => {
    e.preventDefault(); e.stopPropagation(); setDragCat(null);
    if (e.dataTransfer.files && e.dataTransfer.files.length) { uploadFiles(e.dataTransfer.files, category); return; }
    const id = e.dataTransfer.getData(DND_KEY);
    if (id) moveTo(id, category);
  };
  const archiveDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setDragCat(null);
    if (e.dataTransfer.files && e.dataTransfer.files.length) return;   // don't upload into Archive
    const id = e.dataTransfer.getData(DND_KEY);
    if (id) archive(id);
  };

  const drawings = data?.drawings || [];
  const live = drawings.filter((d) => !d.deleted_at);
  const archived = drawings.filter((d) => d.deleted_at);
  const tileProps = { tenderId: tender.id, onView: setViewer, onRename: setRename, bb };

  const dropCls = (active) => ["rounded-lg border transition-colors", active ? "border-2 border-dashed border-accent bg-accent/5" : "border border-transparent"].join(" ");

  return (
    <div className="mt-6" data-testid="drawings-tab">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="font-display text-ink" style={{ fontSize: 15, fontWeight: 600 }}>Drawings</h3>
          <div className="text-ink-3 mt-0.5" style={{ fontSize: 12 }} data-testid="drawings-summary">
            {data ? <>{live.length} drawing{live.length === 1 ? "" : "s"} · {fmtBytes(data.total_bytes)}{archived.length ? ` · ${archived.length} archived` : ""}</> : "…"}
            {" · PDF only, 10 MB each · drag tiles between sections, or onto Archive to supersede"}
          </div>
        </div>
        <input ref={inputRef} type="file" accept="application/pdf,.pdf" multiple className="hidden" data-testid="drawings-file-input"
          onChange={(e) => { uploadFiles(e.target.files, uploadTarget.current); e.target.value = ""; }} />
        <PrimaryButton icon={Upload} onClick={() => pickUpload("drawings")} data-testid="drawings-upload">Upload PDFs</PrimaryButton>
      </div>

      {uploads.length > 0 && (
        <div className="mb-4 rounded-md border border-line bg-surface-2/40 px-3 py-2 space-y-1" data-testid="drawings-upload-progress">
          {uploads.map((u, i) => (
            <div key={i} className="flex items-center gap-2" style={{ fontSize: 12 }} data-testid={`drawings-upload-row-${i}`}>
              {u.state === "uploading" ? <Loader2 size={12} className="animate-spin text-accent" /> : u.state === "done" ? <span className="text-accent">✓</span> : u.state === "failed" ? <span style={{ color: "#7A1F1F" }}>✕</span> : <span className="text-ink-4">·</span>}
              <span className="text-ink truncate">{u.name}</span>
              <span className="text-ink-3">{u.state === "failed" ? u.error : u.state}</span>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div className="text-ink-3" style={{ fontSize: 12 }}>Loading…</div>
      ) : (
        <div className="space-y-5">
          {CATS.map((cat) => {
            const items = live.filter((d) => (d.category || "drawings") === cat.key);
            const active = dragCat === cat.key;
            return (
              <section key={cat.key} data-testid={`drawings-section-${cat.key}`}
                onDragOver={(e) => { e.preventDefault(); if (e.dataTransfer) e.dataTransfer.dropEffect = "move"; setDragCat(cat.key); }}
                onDragLeave={(e) => { if (e.currentTarget === e.target) setDragCat((c) => (c === cat.key ? null : c)); }}
                onDrop={(e) => sectionDrop(e, cat.key)}>
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="flex items-baseline gap-2 min-w-0">
                    <h4 className="font-display text-ink" style={{ fontSize: 13.5, fontWeight: 600 }}>{cat.label}</h4>
                    <span className="text-ink-4" style={{ fontSize: 11 }}>{items.length} · {cat.hint}</span>
                  </div>
                  <button type="button" onClick={() => pickUpload(cat.key)} className="inline-flex items-center gap-1 h-7 px-2 rounded text-ink-2 hover:text-ink hover:bg-surface-2 border border-line" style={{ fontSize: 11.5 }} data-testid={`drawings-upload-${cat.key}`}>
                    <Plus size={12} /> Upload
                  </button>
                </div>
                <div className={dropCls(active)} style={{ padding: items.length ? 8 : 0 }}>
                  {items.length ? (
                    <div style={GRID} data-testid={`drawings-tiles-${cat.key}`}>
                      {items.map((d) => <Tile key={d.id} d={d} {...tileProps} />)}
                    </div>
                  ) : (
                    <div className="rounded-lg border border-dashed border-line px-4 py-6 text-center text-ink-4" style={{ fontSize: 12 }} data-testid={`drawings-empty-${cat.key}`}>
                      Drop PDFs here, drag tiles in, or click <b>Upload</b> to add to {cat.label}.
                    </div>
                  )}
                </div>
              </section>
            );
          })}

          <section data-testid="drawings-section-archive"
            onDragOver={(e) => { e.preventDefault(); if (e.dataTransfer) e.dataTransfer.dropEffect = "move"; setDragCat("archive"); }}
            onDragLeave={(e) => { if (e.currentTarget === e.target) setDragCat((c) => (c === "archive" ? null : c)); }}
            onDrop={archiveDrop}>
            <div className="flex items-center gap-2 mb-2">
              <Archive size={13} className="text-ink-3" />
              <h4 className="font-display text-ink-2" style={{ fontSize: 13.5, fontWeight: 600 }}>Archive</h4>
              <span className="text-ink-4" style={{ fontSize: 11 }}>{archived.length} · superseded drawings — drag here to supersede, or back onto a section to restore</span>
            </div>
            <div className={dropCls(dragCat === "archive")} style={{ padding: archived.length ? 8 : 0 }}>
              {archived.length ? (
                <div style={GRID} data-testid="drawings-tiles-archive">
                  {archived.map((d) => <Tile key={d.id} d={d} {...tileProps} />)}
                </div>
              ) : (
                <div className="rounded-lg border border-dashed border-line px-4 py-5 text-center text-ink-4" style={{ fontSize: 12 }} data-testid="drawings-empty-archive">
                  Drag a superseded drawing here. Archived files are kept and can be viewed or restored anytime.
                </div>
              )}
            </div>
          </section>
        </div>
      )}

      <RenameDialog open={!!rename} drawing={rename} onClose={() => setRename(null)} onSaved={replace} tenderId={tender.id} />
      <PdfViewerModal open={!!viewer} onClose={() => setViewer(null)}
        src={viewer ? `/tenders/${tender.id}/drawings/${viewer.id}/file` : null}
        title={viewer?.title} subtitle={viewer ? `${tender.ref} · ${viewer.page_count ? `${viewer.page_count} pages · ` : ""}${fmtBytes(viewer.size_bytes)}` : ""}
        filename={viewer?.filename}
        onBluebeam={viewer && !viewer.deleted_at ? () => bb.launch(tender.id, viewer) : null}
        testid="drawing-pdf-viewer" />
      {bb.ui}
    </div>
  );
}
